import React from "react";
import { useThemeContext } from "../../context/themeContext";
import {
  Button,
  Card,
  CardActions,
  CardContent,
  Grid,
  Stack,
  SvgIcon,
  Typography,
} from "@mui/material";
import { ReactComponent as EmptyIconDark } from "../../assets/IconEmptyDark.svg";
import { ReactComponent as EmptyIconLight } from "../../assets/IconEmptyLight.svg";
import { getContentNoun, getStatusOptions } from "../../utils/utils";
import { useNavigate } from "react-router-dom";

/**
 * Shown in place of the Track grid when a tab has nothing in it: a no-matches
 * message when filters/search are applied, otherwise "hasn't X any Y yet" plus
 * (on your own library) import and explore prompts. Works for All too, which
 * reads as "anything" / "nothing".
 */
export default function EmptyLibrary({
  filtersApplied,
  status,
  contentType,
  targetUsername,
  isOwn,
}) {
  const { mode } = useThemeContext();
  const navigate = useNavigate();
  // All has no noun of its own ("any things"), so its copy says "anything"
  const isAll = contentType === "all";
  const libraryName = isOwn ? "your library" : `@${targetUsername}'s library`;

  const handleCollectionClick = () => {
    navigate(`/explore`);
  };

  const handleImportClick = () => {
    navigate(`/import`);
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} display="flex" justifyContent="center">
        <Card
          sx={{
            width: "100%",
            boxShadow: "none",
            bgcolor: (theme) =>
              mode === "dark"
                ? theme.palette.background.paper
                : theme.palette.secondary.main,
          }}
        >
          <CardContent
            sx={{
              "&.MuiCardContent-root": {
                pb: 2,
              },
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems="center"
              justifyContent="center"
              spacing={{ xs: 0, sm: 2 }}
            >
              <SvgIcon
                sx={{
                  width: 100,
                  height: 100,
                  color: "primary.main",
                }}
              >
                {mode === "dark" ? <EmptyIconDark /> : <EmptyIconLight />}
              </SvgIcon>
              {filtersApplied ? (
                <Typography textAlign={{ xs: "center", sm: "left" }}>
                  {isAll
                    ? `Nothing in ${libraryName} matches`
                    : `No ${getContentNoun({ type: contentType, plural: true })} in ${libraryName} match`}{" "}
                  your current filters
                  {/* Adding only makes sense in your own library */}
                  {isOwn ? (
                    <>
                      , try again or add{" "}
                      {isAll
                        ? "something new"
                        : `a new ${getContentNoun({ type: contentType, plural: false })}`}{" "}
                      to your library using the add button in the bottom right
                    </>
                  ) : (
                    ", try changing them"
                  )}
                </Typography>
              ) : (
                <Typography textAlign={{ xs: "center", sm: "left" }}>
                  {isOwn ? "You haven't" : `@${targetUsername} hasn't`}{" "}
                  {getStatusOptions()[status].label.toLowerCase()}{" "}
                  {isAll
                    ? "anything"
                    : `any ${getContentNoun({ type: contentType, plural: true })}`}{" "}
                  yet
                  {/* {(status === "finished" || status === "queued") &&
              ". Get started using the add button in the bottom right"} */}
                </Typography>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Grid>
      {isOwn && !filtersApplied && (
        <>
          <Grid item xs={6} display="flex" justifyContent="center">
            <Card
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                boxShadow: "none",
                bgcolor: (theme) =>
                  mode === "dark"
                    ? theme.palette.background.paper
                    : theme.palette.secondary.main,
              }}
            >
              <CardContent sx={{ flex: 1 }}>
                Already tracking your{" "}
                {getContentNoun({ type: contentType, plural: true })} elsewhere?
                Import your library now...
              </CardContent>
              <CardActions sx={{ justifyContent: "flex-end" }}>
                <Button onClick={handleImportClick}>Import library</Button>
              </CardActions>
            </Card>
          </Grid>
          <Grid item xs={6} display="flex" justifyContent="center">
            <Card
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                boxShadow: "none",
                bgcolor: (theme) =>
                  mode === "dark"
                    ? theme.palette.background.paper
                    : theme.palette.secondary.main,
              }}
            >
              <CardContent sx={{ flex: 1 }}>
                Or explore curated collections and build your library from
                there...
              </CardContent>
              <CardActions sx={{ justifyContent: "flex-end" }}>
                <Button onClick={handleCollectionClick}>
                  Explore collections
                </Button>
              </CardActions>
            </Card>
          </Grid>
        </>
      )}
    </Grid>
  );
}
